import { Check } from "lucide-react";
import { cn } from "@/modern/utils";
import type { SurfaceSolutionTag } from "../api/types";

type Props = {
  tags: SurfaceSolutionTag[];
  value: number[];
  onChange: (ids: number[]) => void;
};

export default function PromoteSurfaceTagPicker({ tags, value, onChange }: Props) {
  const toggle = (id: number) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  if (!tags.length) {
    return <p className="text-sm text-muted-foreground">No solution tags available.</p>;
  }

  return (
    <div
      className="max-h-40 overflow-y-auto [scrollbar-width:thin] sm:max-h-52"
      role="group"
      aria-label="Solution tags"
    >
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const active = value.includes(tag.id);
          return (
            <button
              key={tag.id}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(tag.id)}
              className={cn(
                "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border-2 px-3.5 py-2 text-sm font-semibold shadow-sm transition",
                "active:scale-[0.97]",
                active
                  ? "border-primary bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
                  : "border-foreground/20 bg-card text-foreground hover:border-foreground/40 hover:bg-muted hover:shadow-md"
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 items-center justify-center rounded-full border transition",
                  active
                    ? "border-primary-foreground/40 bg-primary-foreground/20"
                    : "border-foreground/25 bg-transparent"
                )}
                aria-hidden
              >
                {active && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              {tag.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
